/**
 * CustomerList - Virtualized customer list.
 *
 * Wraps the shared DataTable component with customer-specific columns and renderers.
 * Supports single selection (inspector) and multi-selection (bulk actions).
 */

import { useCallback, forwardRef, type CSSProperties, type ReactNode } from 'react'
import { format, formatDistanceToNow } from 'date-fns'
import { Copy } from 'lucide-react'
import { DataTable, type ColumnDef, type SortState, type DataTableHandle } from '@/shared/components/DataTable'
import { StatusDot } from '@/shared/components/StatusDot'
import { EmptyState } from '@/shared/components/EmptyState'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/shared/components/ui/tooltip'
import { ToastAction } from '@/shared/components/ui/toast'
import { useToast } from '@/shared/hooks'
import { ApiClientError } from '@/lib/api'
import { useCustomersStore, selectHasActiveFilters } from '../store'
import {
  customerDisplayName,
  effectiveLastSeenAt,
  effectiveStatus,
  lastSeenSourceLabel,
  primaryConsumerId,
  statusLabel,
} from '../display'
import type { Customer, CustomerStatus } from '../types'
import { InlineEditCell } from './InlineEditCell'
import { InlineSelectCell } from './InlineSelectCell'

// ── Column Definitions ──────────────────────────────────────────────────────

function createColumns(): ColumnDef<Customer>[] {
  return [
    {
      id: 'name',
      label: 'Name',
      accessor: (row) => customerDisplayName(row),
      width: '24%',
      minWidth: 180,
      sortable: true,
    },
    {
      id: 'email',
      label: 'Email',
      accessor: 'email',
      width: '26%',
      minWidth: 200,
      sortable: true,
    },
    {
      id: 'status',
      label: 'Status',
      accessor: (row) => effectiveStatus(row),
      width: 130,
      sortable: true,
    },
    {
      id: 'consumer',
      label: 'Consumer',
      accessor: (row) => primaryConsumerId(row) ?? '',
      width: 180,
      sortable: false,
    },
    {
      id: 'last_seen_at',
      label: 'Last Active',
      accessor: (row) => effectiveLastSeenAt(row) ?? 0,
      width: 140,
      sortable: true,
    },
    {
      id: 'id',
      label: 'ID',
      accessor: 'id',
      width: 120,
    },
  ]
}

// ── Component Props ─────────────────────────────────────────────────────────

interface CustomerListProps {
  /** Called when focused row changes (for inspector sync) */
  onFocusedRowChange?: (customerId: string | null) => void
}

// ── Component ───────────────────────────────────────────────────────────────

export const CustomerList = forwardRef<DataTableHandle, CustomerListProps>(
  function CustomerList({ onFocusedRowChange }, ref): ReactNode {
    const { toast } = useToast()

    // Store state
    const customers = useCustomersStore((s) => s.customers)
    const listLoading = useCustomersStore((s) => s.listLoading)
    const selectedIds = useCustomersStore((s) => s.selectedIds)
    const selectedId = useCustomersStore((s) => s.selectedId)
    const sortState = useCustomersStore((s) => s.sortState)
    const hasActiveFilters = useCustomersStore(selectHasActiveFilters)

    // Store actions
    const selectCustomer = useCustomersStore((s) => s.selectCustomer)
    const toggleSelection = useCustomersStore((s) => s.toggleSelection)
    const setSort = useCustomersStore((s) => s.setSort)
    const clearFilters = useCustomersStore((s) => s.clearFilters)
    const inlineUpdate = useCustomersStore((s) => s.inlineUpdate)

  // Handle ID copy
  const handleCopyId = useCallback(
    (id: string) => {
      navigator.clipboard.writeText(id)
      toast({ title: 'Copied', description: 'Customer ID copied to clipboard' })
    },
    [toast]
  )

  const columns = createColumns()

  // Handle row click
  const handleRowClick = useCallback(
    (row: Customer) => {
      selectCustomer(row.id)
    },
    [selectCustomer]
  )

  // Handle selection change
  const handleSelectionChange = useCallback(
    (ids: string[]) => {
      // Sync with store
      ids.forEach((id) => {
        if (!selectedIds.has(id)) {
          toggleSelection(id)
        }
      })
      selectedIds.forEach((id) => {
        if (!ids.includes(id)) {
          toggleSelection(id)
        }
      })
    },
    [selectedIds, toggleSelection]
  )

  // Handle sort change
  const handleSortChange = useCallback(
    (sort: SortState) => {
      setSort({
        column: sort.column as typeof sortState.column,
        direction: sort.direction,
      })
    },
    [setSort]
  )

  // Handle inline edit — only `name` and `status` are accepted by the
  // backend PATCH endpoint (per `services/identity/api/openapi.yaml`).
  const handleInlineEdit = useCallback(
    async (
      id: string,
      patch: Partial<Pick<Customer, 'name' | 'status'>>
    ) => {
      // Capture the previous value BEFORE optimistic update so the undo handler
      // can revert to the true original.
      const previous = customers.find((c) => c.id === id)
      const fieldName = Object.keys(patch)[0] as keyof typeof patch
      const previousValue = previous?.[fieldName]

      try {
        await inlineUpdate(id, patch)

        const fieldLabels: Record<string, string> = {
          name: 'name',
          status: 'status',
        }
        const fieldLabel = fieldLabels[fieldName] ?? fieldName

        toast({
          title: `Updated ${fieldLabel}`,
          description: previousValue
            ? `Changed from "${previousValue}"`
            : undefined,
        })
      } catch (err) {
        // Use structured status code from ApiClientError when available;
        // fall back to message-based detection for non-API errors.
        const isConflict =
          (err instanceof ApiClientError && err.statusCode === 409) ||
          (err instanceof Error && err.message.includes('409'))

        if (isConflict) {
          toast({
            title: 'Conflict detected',
            description: 'This customer was updated elsewhere. Click to reload.',
            variant: 'danger',
            action: (
              <ToastAction altText="Reload page" onClick={() => window.location.reload()}>
                Reload
              </ToastAction>
            ),
          })
        } else {
          const errorMessage =
            err instanceof Error ? err.message : 'Failed to save changes'
          toast({
            title: 'Update failed',
            description: errorMessage,
            variant: 'danger',
          })
        }
      }
    },
    [inlineUpdate, customers, toast]
  )

  // Custom cell renderer
  const renderCell = useCallback(
    (column: ColumnDef<Customer>, row: Customer): ReactNode => {
      switch (column.id) {
        case 'name': {
          const lastSeen = effectiveLastSeenAt(row)
          const isOnline = lastSeen
            ? Date.now() / 1000 - lastSeen < 300 // Active in last 5 min
            : false
          const placeholder = row.email ? row.email.split('@')[0] : row.id.slice(0, 12)

          return (
            <div style={styles.nameCell}>
              {isOnline && <StatusDot kind="success" size={8} pulse />}
              <InlineEditCell
                value={row.name ?? ''}
                placeholder={placeholder}
                onSave={(newName) => handleInlineEdit(row.id, { name: newName })}
              />
            </div>
          )
        }

        case 'email':
          // Email is read-only — backend does not allow editing via PATCH.
          return (
            <span style={styles.emailText} title={row.email}>
              {row.email}
            </span>
          )

        case 'status': {
          const current = effectiveStatus(row)
          // 'unknown' is a synthetic value for empty backend status; the
          // operator can set a real status from the dropdown which will
          // overwrite it.
          const displayValue = current === 'unknown' ? '' : current
          return (
            <InlineSelectCell
              value={displayValue}
              placeholder={statusLabel(row)}
              options={[
                { value: 'lead', label: 'Lead' },
                { value: 'registered', label: 'Registered' },
                { value: 'active', label: 'Active' },
                { value: 'churned', label: 'Churned' },
              ]}
              onSave={(newStatus) =>
                handleInlineEdit(row.id, {
                  status: newStatus as Exclude<CustomerStatus, ''>,
                })
              }
            />
          )
        }

        case 'consumer': {
          const consumer = primaryConsumerId(row)
          if (!consumer) {
            return <span style={styles.muted}>—</span>
          }
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <span style={styles.consumerText} title={consumer}>
                  {consumer}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {row.origin?.channel
                  ? `Channel: ${row.origin.channel}`
                  : 'Primary consumer'}
              </TooltipContent>
            </Tooltip>
          )
        }

        case 'last_seen_at': {
          const lastSeen = effectiveLastSeenAt(row)
          if (!lastSeen) {
            return <span style={styles.muted}>Never</span>
          }
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <span style={styles.dateText}>
                  {formatDistanceToNow(new Date(lastSeen * 1000), {
                    addSuffix: true,
                  })}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {`${lastSeenSourceLabel(row)}: ${format(
                  new Date(lastSeen * 1000),
                  'PPpp'
                )}`}
              </TooltipContent>
            </Tooltip>
          )
        }

        case 'id':
          return (
            <button
              type="button"
              style={styles.idButton}
              onClick={(e) => {
                e.stopPropagation()
                handleCopyId(row.id)
              }}
              title="Copy ID"
            >
              <span style={styles.idText}>{row.id.slice(0, 12)}...</span>
              <Copy size={12} />
            </button>
          )

        default:
          return null
      }
    },
    [handleCopyId, handleInlineEdit]
  )

  // Handle focused row change for inspector sync
  const handleFocusedRowChange = useCallback(
    (rowId: string | null) => {
      onFocusedRowChange?.(rowId)
    },
    [onFocusedRowChange]
  )

  return (
    <div style={styles.container}>
      <DataTable
        ref={ref}
        columns={columns}
        data={customers}
        getRowId={(row) => row.id}
        loading={listLoading}
        density="compact"
        selection="multi"
        selectedIds={Array.from(selectedIds)}
        sortState={{
          column: sortState.column,
          direction: sortState.direction,
        }}
        onRowClick={handleRowClick}
        onSelectionChange={handleSelectionChange}
        onSortChange={handleSortChange}
        onFocusedRowChange={handleFocusedRowChange}
        renderCell={renderCell}
        activeRowId={selectedId}
        emptyState={
          <EmptyState
            variant="first-use"
            icon="users"
            title="No customers yet"
            description="Customers appear here when they sign up via webhook or are imported."
          />
        }
        filteredEmptyState={
          hasActiveFilters ? (
            <EmptyState
              variant="filtered"
              title="No customers match these filters"
              action={
                <button
                  type="button"
                  onClick={clearFilters}
                  style={styles.clearButton}
                >
                  Clear filters
                </button>
              }
            />
          ) : undefined
        }
      />
    </div>
  )
})

// ── Styles ──────────────────────────────────────────────────────────────────

const styles: Record<string, CSSProperties> = {
  container: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    padding: '0 24px 24px',
  },
  nameCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: 0,
    flex: 1,
  },
  dateText: {
    fontSize: '12px',
    color: 'var(--text-secondary)',
  },
  emailText: {
    fontSize: '13px',
    color: 'var(--text-primary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'block',
  },
  consumerText: {
    fontSize: '11px',
    fontFamily: "'JetBrains Mono', monospace",
    color: 'var(--text-muted)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'block',
    maxWidth: '100%',
  },
  muted: {
    fontSize: '12px',
    color: 'var(--text-muted)',
    fontStyle: 'italic',
  },
  idButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 6px',
    fontSize: '11px',
    fontFamily: "'JetBrains Mono', monospace",
    color: 'var(--text-muted)',
    background: 'var(--bg-tertiary)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    transition: 'all var(--transition-fast)',
  },
  idText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  clearButton: {
    padding: '4px 12px',
    fontSize: '13px',
    color: 'var(--accent-primary)',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    textDecoration: 'underline',
  },
}

export default CustomerList
